import { useEffect, useState, useRef, createRef } from 'react';
import PropTypes from 'prop-types';
import { useResizeObserver } from 'usehooks-ts';
import Checkboxes from '@/components/checkboxes';
import Control from '@/components/control';
import Form from '@/components/form';
import QuestionnaireItem from '@/components/questionnaire-item';
import Radio from '@/components/radio';
import TextField from '@/components/text-field';
import usePrevious from '@/hooks/usePrevious';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import '@/components/questionnaire/questionnaire.css';
import { getElementSize } from '@/utils/helper';
import useAppRefs from '@/hooks/useAppRefs';

const DIVIDER = ' ; ';

export default function Questionnaire(props) {
  const dialog = useAppSelector(ConfigSlice.selectors.additionalQuestions);
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const [answers, setAnswers] = useState({});
  const [path, setPath] = useState(getRootQuestionPath(props.questions, answers, DIVIDER));
  const [index, setIndex] = useState(0);
  const questionRefs = useRef(props.questions.map(() => createRef()));
  const questionnaireRef = useRef(null);
  const [isRequiredShown, setIsRequiredShown] = useState(false);
  const prevPath = usePrevious(path, (prev, curr) => prev.join() === curr.join(), true);
  const { dialogContentRef, quizRef } = useAppRefs();

  const { height: dialogContentHeight } = useResizeObserver({ ref: dialogContentRef, box: 'border-box' });
  const { height: questionnaireHeight } = useResizeObserver({ ref: questionnaireRef, box: 'border-box' });
  const { height: quizHeight } = useResizeObserver({ ref: quizRef, box: 'border-box' });

  useEffect(() => {
    scroll();
  }, [prevPath]);

  useEffect(() => {
    scroll();
    setIsRequiredShown(false);
    if (index === path.length) {
      const currentKey = path[index - 1];
      const previousKey = path[index - 2];
      if (!dialog?.postAfterEach) {
        props.onComplete(answers);
      }
      if (
        answers.hasOwnProperty(currentKey) ||
        answers.hasOwnProperty('city') ||
        answers.hasOwnProperty('street') ||
        answers.hasOwnProperty('streetNumber') ||
        answers.hasOwnProperty('zipCode')
      ) {
        props.onNext(currentKey, previousKey, answers, true);
      }
    } else {
      props.onNext(path[index], path[index - 1], answers, false);
      setPath(getUpdatedPath(props.questions, answers, path, path[index], DIVIDER));
    }
    window.addEventListener('keypress', keyPressEventListener);
    return () => {
      window.removeEventListener('keypress', keyPressEventListener);
    };
  }, [index, answers]);

  function scroll() {
    if (dialogContentRef.current && questionRefs.current[index]) {
      const sizes = questionRefs.current.map((questionRef) => getElementSize(questionRef, 'height'));
      const questionsAbove = sizes.slice(0, index).reduce((sum, n) => sum + n, 0);
      const outOfQuestionnaire = Number.parseInt(quizHeight - questionnaireHeight);
      const contentWindowCompensation = Number.parseInt(dialogContentHeight / 5);
      if (index >= 1) {
        dialogContentRef.current.scroll({
          top: questionsAbove + outOfQuestionnaire - contentWindowCompensation,
          behavior: 'smooth',
        });
      } else {
        dialogContentRef.current.scroll({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  }

  function keyPressEventListener(e) {
    if (e.key === 'Enter' && index < path.length) {
      const isRequired = props.questions[index].isRequired;
      const key = props.questions[index].key;
      const type = props.questions[index].type;
      if (isRequired && type === 'form') {
        const validation = props.questions[index].schema.reduce(
          (keys, row) => [...keys, ...row.map((el) => el.validate(answers[el.key] || ''))],
          [],
        );
        if (validation.some((v) => !v)) {
          setIsRequiredShown(true);
          return;
        }
      }
      if (isRequired && type !== 'form' && answers[key] === undefined) {
        setIsRequiredShown(true);
        return;
      }
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      if (path.length - 1 === index) {
        props.onNext(path[index], undefined, undefined, true);
      } else {
        props.onNext(path[index], undefined, undefined, false);
      }
      setIndex(index + 1);
    }
  }

  function isArray(array) {
    return Object.prototype.toString.call(array) === '[object Array]';
  }

  function guardQuestion(question) {
    if ((question.type === 'radio' || question.type === 'checkbox') && !question.choices) {
      throw new Error('Question of radio and checkbox type must have choices');
    }
  }

  function mergePaths(...paths) {
    return paths.reduce((mergedPath, item) => {
      item.forEach((i) => {
        if (mergedPath.indexOf(i) === -1) mergedPath.push(i);
      });
      return mergedPath;
    }, []);
  }

  function mergePathsWithReplacement(...paths) {
    const allPath = paths.reduce((mergedPath, item) => {
      item.forEach((i) => {
        mergedPath.push(i);
      });
      return mergedPath;
    }, []);
    return Array.from(new Set(allPath.reverse())).reverse();
  }

  function getPathQuestionIndex(path, key) {
    return path.indexOf(key);
  }

  function getRootQuestionIndex(questions) {
    const index = questions.findIndex((question) => question.isRoot);
    return index >= 0 ? index : 0;
  }

  function getPathFromCheckboxChoices(choices, answer) {
    if (choices.length && answer.length) {
      const paths = answer.map((a) => {
        const choice = choices.find((choice) => choice.value === a);
        return choice ? choice.path : undefined;
      });
      if (paths.every((path) => !isArray(path))) {
        return undefined;
      } else {
        return paths.reduce((path, item) => {
          if (isArray(item)) {
            return mergePathsWithReplacement(path, item);
          }
          return path;
        }, []);
      }
    } else {
      return undefined;
    }
  }

  function getPathFromChoices(question, answers, divider) {
    const answer = answers[question.key] || '';
    const choices = question.choices || [];
    switch (question.type) {
      case 'input':
      case 'textarea':
      case 'address':
        return undefined;
      case 'checkbox':
        return getPathFromCheckboxChoices(choices, answer.split(divider));
      case 'radio':
        return getPathFromRadioChoices(choices, answer);
    }
  }

  function getRootQuestionPath(questions, answers, divider) {
    const index = getRootQuestionIndex(questions);
    const question = questions[index];
    const path = question.path;
    const key = question.key;
    const pathChoices = getPathFromChoices(question, answers, divider);
    let withRootPath = [question.key];
    if (path === undefined) {
      const restQuestions = questions.slice(index, questions.length);
      const restQuestionsKey = restQuestions.map((q) => q.key);
      withRootPath = mergePaths(withRootPath, restQuestionsKey);
    }
    if (path && path.length > 0) {
      withRootPath = mergePaths(withRootPath, path);
    }
    if (path && path.length <= 0) {
      withRootPath = mergePaths(withRootPath);
    }
    let withChoicesPath = [];
    if (pathChoices === undefined) {
      withChoicesPath = mergePaths(withRootPath);
    }
    if (pathChoices && pathChoices.length > 0) {
      withChoicesPath = mergePaths([question.key], pathChoices);
    }
    if (pathChoices && pathChoices.length <= 0) {
      withChoicesPath = mergePaths(withRootPath.slice(0, withRootPath.findIndex((k) => k === key) + 1));
    }
    return withChoicesPath;
  }

  function getPathFromRadioChoices(choices, answer) {
    if (choices.length && answer) {
      const choice = choices.find((choice) => choice.value === answer);
      if (choice) {
        return choice.path;
      } else return undefined;
    } else {
      return undefined;
    }
  }

  function getPassedPath(path, index) {
    return path.slice(0, index + 1);
  }

  function getQuestionByKey(questions, key) {
    return questions.find((question) => question.key === key) || null;
  }

  function getUpdatedPath(questions, answers, path, key, divider) {
    const index = path.indexOf(key);
    const passedPath = getPassedPath(path, index);
    return passedPath.reduce(
      (historyOPath, key, index) => {
        const question = getQuestionByKey(questions, key);
        if (question) {
          let withRoot = [...historyOPath];
          const questionPath = question.path;
          const choicesPath = getPathFromChoices(question, answers, divider);
          if (questionPath === undefined) {
            withRoot = [...withRoot];
          }
          if (questionPath && questionPath.length > 0) {
            withRoot = mergePaths(withRoot, questionPath);
          }
          if (questionPath && questionPath.length <= 0) {
            withRoot = getPassedPath(withRoot, index);
          }
          if (choicesPath === undefined) {
            withRoot = [...withRoot];
          }
          if (choicesPath && choicesPath.length > 0) {
            withRoot = mergePaths(getPassedPath(withRoot, index), choicesPath);
          }
          if (choicesPath && choicesPath.length <= 0) {
            withRoot = getPassedPath(withRoot, index);
          }
          return withRoot;
        } else {
          throw new Error(`No question with key [${key}] in path`);
        }
      },
      [passedPath[0]],
    );
  }

  function isQuestionLast(path, key) {
    return getPathQuestionIndex(path, key) === path.length - 1;
  }

  function isQuestionActive(path, index, key) {
    return key === path[index];
  }

  function isQuestionClickable(path, index, key) {
    const passedPath = getPassedPath(path, index);
    return passedPath.indexOf(key) !== -1;
  }

  // Question click handlers

  function onHeadClick(index) {
    setIndex(index);
  }

  function onMainClick(index, type) {
    setIndex(type === 'radio' ? index + 1 : index);
  }

  function onNextClick() {
    const isRequired = props.questions[index].isRequired;
    const key = props.questions[index].key;
    const type = props.questions[index].type;
    if (isRequired && type === 'form') {
      const validation = props.questions[index].schema.reduce(
        (keys, row) => [...keys, ...row.map((el) => el.validate(answers[el.key] || ''))],
        [],
      );
      if (validation.some((v) => !v)) {
        setIsRequiredShown(true);
        return;
      }
    }
    if (isRequired && type !== 'form' && answers[key] === undefined) {
      setIsRequiredShown(true);
      return;
    }
    if (path.length - 1 === index) {
      props.onNext(path[index], undefined, undefined, true);
    } else {
      props.onNext(path[index], undefined, undefined, false);
    }
    setIndex(index + 1);
  }

  // Questions answer functions

  function onCheckboxUpdate(name, checkboxName, checkboxValue) {
    const answer = answers[name] || '';
    const updatedAnswer = checkboxValue
      ? [...answer.split(DIVIDER).filter((k) => k), checkboxName].join(DIVIDER)
      : answer
          .split(DIVIDER)
          .filter((k) => k !== checkboxName)
          .join(DIVIDER);
    const updatedAnswers = { ...answers, [name]: updatedAnswer };
    const updatedPath = getUpdatedPath(props.questions, updatedAnswers, path, name, DIVIDER);
    setAnswers(updatedAnswers);
    setPath(updatedPath);
  }

  function onRadioUpdate(name, value, type) {
    const updatedAnswers = { ...answers, [name]: value };
    const updatedPath = getUpdatedPath(props.questions, updatedAnswers, path, name, DIVIDER);
    setAnswers(updatedAnswers);
    setPath(updatedPath);
    if (type === 'click') {
      props.onNext(name);
      setIndex(index + 1);
    }
  }

  function onRadioCheckboxV2Click(value, id, source) {
    if (typeof value === 'string') {
      const updatedAnswers = { ...answers, [id]: value };
      const updatedPath = getUpdatedPath(props.questions, updatedAnswers, path, id, DIVIDER);
      setAnswers(updatedAnswers);
      setPath(updatedPath);
      if (source === 'mouse') {
        props.onNext(id);
        setIndex(index + 1);
      }
    } else {
      // checkbox (multiple choice) - only update the answer/path, don't auto-advance;
      // the user clicks "Weiter" (onNextClick) to move on, same as the v1 checkbox flow.
      const updatedAnswers = { ...answers, [id]: value.join(DIVIDER) };
      const updatedPath = getUpdatedPath(props.questions, updatedAnswers, path, id, DIVIDER);
      setAnswers(updatedAnswers);
      setPath(updatedPath);
    }
  }

  function onAnswerUpdate(name, value) {
    const updatedAnswers = { ...answers, ...value };
    const updatedPath = getUpdatedPath(props.questions, updatedAnswers, path, name, DIVIDER);
    setAnswers(updatedAnswers);
    setPath(updatedPath);
  }

  function onInputUpdate(value, name, source) {
    setAnswers({ ...answers, [name]: value });
  }

  // Render content for Question

  function getQuestionContent(question) {
    guardQuestion(question);
    const questionIndex = getPathQuestionIndex(path, question.key);
    switch (question.type) {
      case 'form':
        return (
          <Form
            isDisabled={questionIndex > index}
            name={question.key}
            schema={question.schema}
            onChange={onAnswerUpdate}
          />
        );
      case 'checkbox':
        return cssVersion === 1 ? (
          <Checkboxes
            isDisabled={questionIndex > index}
            name={question.key}
            orientation={question.orientation}
            value={answers[question.key] ? answers[question.key].split(DIVIDER) : []}
            choices={question.choices}
            onChange={onCheckboxUpdate}
          />
        ) : (
          <Control.CardText
            id={question.key}
            value={answers[question.key] ? answers[question.key].split(DIVIDER) : []}
            choices={question.choices.map((choice) => ({ label: choice.label, value: choice.value }))}
            onChange={onRadioCheckboxV2Click}
          />
        );
      case 'radio':
        return cssVersion === 1 ? (
          <Radio
            isDisabled={questionIndex > index}
            name={question.key}
            orientation={question.orientation}
            value={answers[question.key]}
            list={question.choices}
            onChange={onRadioUpdate}
          />
        ) : (
          <Control.CardText
            state='idle'
            disabled={false}
            id={question.key}
            value={answers[question.key] || ''}
            choices={question.choices.map((choice) => ({ label: choice.label, value: choice.value }))}
            onChange={onRadioCheckboxV2Click}
          />
        );
      case 'input':
        return cssVersion === 1 ? (
          <TextField
            isDisabled={questionIndex > index}
            name={question.key}
            value={answers[question.key]}
            onChange={onInputUpdate}
          />
        ) : (
          <Control.Text
            label={question.label}
            id={question.key}
            value={answers[question.key] || ''}
            onChange={onInputUpdate}
          />
        );
    }
  }

  function getSortedQuestions() {
    const rawPath = path.map((p) => props.questions.find((q) => q.key === p));
    return rawPath.filter((q) => typeof q !== 'undefined');
  }

  return (
    <div ref={questionnaireRef} data-css-version={cssVersion} className='questionnaire'>
      {props.questions
        .reduce((sortedQuestions, question) => {
          if (!path.includes(question.key)) sortedQuestions.push(question);
          return sortedQuestions;
        }, getSortedQuestions())
        .filter((question) => path.includes(question.key))
        .map((question, i) => (
          <div key={question.key} data-q={question.key} ref={questionRefs.current[i]}>
            <QuestionnaireItem
              index={getPathQuestionIndex(path, question.key)}
              type={question.type}
              isRequiredShown={isRequiredShown && i === index}
              requiredMessage={question.requiredMessage}
              isLast={isQuestionLast(path, question.key)}
              isActive={isQuestionActive(path, index, question.key)}
              isClickable={isQuestionClickable(path, index, question.key)}
              title={question.title}
              description={question.description}
              onNextClick={onNextClick}
              onHeadClick={onHeadClick}
              onMainClick={onMainClick}
            >
              {getQuestionContent(question)}
            </QuestionnaireItem>
          </div>
        ))}
    </div>
  );
}

Questionnaire.propTypes = {
  questions: PropTypes.array,
  onNext: PropTypes.func,
  onComplete: PropTypes.func,
};

Questionnaire.displayName = 'Questionnaire';
